/**
 * The account books (P1-TR3, §9.1 item 9).
 *
 * Cash, bank and bKash, each with a running balance. This is success criterion
 * S1 made visible: a partner opens the app and sees where the farm's money is.
 *
 * Every figure on this screen is rendered exactly as the server sent it. The
 * client sums nothing — not the balances, not the running total, not the
 * closing figure. That is not laziness about arithmetic; it is MONEY-3. A
 * balance computed in two places is a balance that can disagree with itself,
 * and the whole system exists because four partners could not agree on a
 * number.
 */

import { useState } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { errorMessageKey } from '../../lib/apiError.js';
import { formatMoney, isNegativeAmount } from '../../lib/currency.js';
import { formatDate } from '../../lib/date.js';
import { useAuth } from '../auth/AuthContext.js';
import { useAccountLedger, useAccounts, useCreateAccount, useUpdateAccount, type Account } from './api.js';

const TYPES = ['cash', 'bank', 'mfs'] as const;

/**
 * Adding a money account (BR-05).
 *
 * BR-05 always said the seeded three were extendable and nothing implemented
 * the extending, so a second bank or a Nagad wallet meant a database edit —
 * and what a partner does instead is post the money to whichever account is
 * closest, which makes the reconciliation routine answer a question nobody
 * asked.
 *
 * There is no opening balance field, and its absence is the point. Balances are
 * derived from approved transactions (MONEY-3), so a new account starts at zero
 * by construction; money arrives in it as a contribution or a transfer that
 * somebody records, not as a number typed here.
 */
function AccountForm({ onDone }: { onDone: () => void }): JSX.Element {
  const { t } = useTranslation();
  const createAccount = useCreateAccount();

  const [name, setName] = useState('');
  const [type, setType] = useState<(typeof TYPES)[number]>('bank');
  const [number, setNumber] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const canSubmit = name.trim().length >= 2 && !createAccount.isPending;

  async function submit(): Promise<void> {
    setErrorKey(null);

    try {
      await createAccount.mutateAsync({
        name: name.trim(),
        type,
        ...(number.trim() === '' ? {} : { number: number.trim() }),
      });
      onDone();
    } catch (error) {
      setErrorKey(errorMessageKey(error));
    }
  }

  return (
    <form
      className="mb-4 rounded-xl bg-white p-4 shadow-sm"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      noValidate
    >
      <h2 className="mb-3 text-base font-semibold text-gray-800">{t('finance.books.addTitle')}</h2>

      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="account-name">
        {t('finance.books.name')}
      </label>
      <input
        id="account-name"
        value={name}
        onChange={(event) => setName(event.target.value)}
        maxLength={50}
        className="mb-3 min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3"
      />

      <fieldset className="mb-3">
        <legend className="mb-2 text-sm font-medium text-gray-700">{t('finance.books.type')}</legend>
        <div className="flex flex-wrap gap-2">
          {TYPES.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setType(option)}
              aria-pressed={type === option}
              className={`min-h-touch rounded-full px-4 py-2 text-sm ${
                type === option ? 'bg-green-700 font-medium text-white' : 'bg-white text-gray-700 shadow-sm'
              }`}
            >
              {t(`finance.books.type.${option}`)}
            </button>
          ))}
        </div>
      </fieldset>

      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="account-number">
        {t('finance.books.number')}
      </label>
      <input
        id="account-number"
        value={number}
        onChange={(event) => setNumber(event.target.value)}
        maxLength={50}
        className="mb-1 min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3"
      />
      <p className="mb-3 mt-1 text-xs text-gray-500">{t('finance.books.startsAtZero')}</p>

      {errorKey !== null && (
        <div role="alert" className="mb-3 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p>{t(errorKey)}</p>
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="min-h-[52px] flex-1 rounded-xl bg-green-700 text-base font-medium text-white disabled:opacity-50"
        >
          {t(createAccount.isPending ? 'finance.books.saving' : 'finance.books.save')}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="min-h-touch rounded-xl bg-gray-100 px-4 py-2 text-sm text-gray-700"
        >
          {t('app.cancel')}
        </button>
      </div>
    </form>
  );
}

/** Retiring an account, never deleting it — the ledger behind it survives. */
function RetireButton({ account }: { account: Account }): JSX.Element {
  const { t } = useTranslation();
  const update = useUpdateAccount();

  return (
    <button
      type="button"
      onClick={() => void update.mutateAsync({ id: account.id, changes: { isActive: !account.isActive } })}
      disabled={update.isPending}
      className={`mb-3 ml-4 min-h-touch rounded-lg px-3 py-1.5 text-sm disabled:opacity-50 ${
        account.isActive ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'
      }`}
    >
      {t(account.isActive ? 'finance.books.retire' : 'finance.books.reopen')}
    </button>
  );
}

export function AccountBooks(): JSX.Element {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const { user } = useAuth();
  const { data: accounts = [], isLoading, isError, refetch } = useAccounts();
  const { data: ledger, isLoading: ledgerLoading } = useAccountLedger(openId);

  // BR-05 — a new account changes the shape of the books, so it is the admin's
  // call. The server enforces the same.
  const canManage = user?.role === 'admin';

  return (
    <div className="page-container">
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('finance.books.title')}</h1>

      {canManage &&
        (isAdding ? (
          <AccountForm onDone={() => setIsAdding(false)} />
        ) : (
          <button
            type="button"
            onClick={() => setIsAdding(true)}
            className="mb-4 flex min-h-touch w-full items-center justify-between rounded-xl bg-green-700 px-4 py-3 text-white"
          >
            <span className="text-sm font-medium">{t('finance.books.add')}</span>
            <span aria-hidden="true">+</span>
          </button>
        ))}

      {isLoading && (
        <p role="status" className="py-8 text-center text-gray-500">
          {t('app.loading')}
        </p>
      )}

      {isError && (
        <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p className="mb-2">{t('error.network')}</p>
          <button type="button" onClick={() => void refetch()} className="min-h-touch font-medium underline">
            {t('app.retry')}
          </button>
        </div>
      )}

      <ul className="space-y-2">
        {accounts.map((account) => (
          <li key={account.id} className={`rounded-xl bg-white shadow-sm ${account.isActive ? '' : 'opacity-70'}`}>
            <button
              type="button"
              onClick={() => setOpenId(openId === account.id ? null : account.id)}
              aria-expanded={openId === account.id}
              className="flex min-h-touch w-full items-center justify-between gap-3 px-4 py-4 text-left"
            >
              <span className="font-medium text-gray-800">
                {account.name}
                {!account.isActive && (
                  <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-normal text-gray-600">
                    {t('finance.books.retired')}
                  </span>
                )}
              </span>
              <span
                className={`tabular-nums text-lg font-semibold ${
                  isNegativeAmount(account.balance) ? 'text-red-700' : 'text-gray-900'
                }`}
              >
                {formatMoney(account.balance)}
              </span>
            </button>

            {canManage && <RetireButton account={account} />}

            {openId === account.id && (
              <div className="border-t border-gray-100 px-4 py-3">
                {ledgerLoading && <p className="py-4 text-center text-sm text-gray-500">{t('app.loading')}</p>}

                {ledger && (
                  <>
                    <p className="mb-2 flex justify-between text-xs text-gray-500">
                      <span>{t('finance.books.opening')}</span>
                      <span className="tabular-nums">{formatMoney(ledger.openingBalance)}</span>
                    </p>

                    {ledger.entries.length === 0 && (
                      <p className="py-4 text-center text-sm text-gray-500">{t('finance.books.empty')}</p>
                    )}

                    <ul className="divide-y divide-gray-100">
                      {ledger.entries.map((entry) => (
                        <li key={entry.id} className="flex items-baseline justify-between gap-2 py-2 text-sm">
                          <span className="min-w-0 flex-1 truncate text-gray-600">
                            {formatDate(entry.txnDate)}
                            {entry.note !== null && ` · ${entry.note}`}
                          </span>
                          <span
                            className={`shrink-0 tabular-nums ${
                              isNegativeAmount(entry.effect) ? 'text-red-700' : 'text-green-700'
                            }`}
                          >
                            {formatMoney(entry.effect, { symbol: false })}
                          </span>
                          {/* The server's running balance, not one accumulated here. */}
                          <span className="w-24 shrink-0 text-right tabular-nums text-gray-800">
                            {formatMoney(entry.runningBalance, { symbol: false })}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <p className="mt-2 flex justify-between border-t border-gray-200 pt-2 text-sm font-medium">
                      <span>{t('finance.books.closing')}</span>
                      <span className="tabular-nums">{formatMoney(ledger.closingBalance)}</span>
                    </p>
                  </>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
